import { useEffect, useState } from "react";
import {
  Modal, Stack, Group, Text, TextInput, Textarea, Button, Select,
  Pill, Box, Divider, SegmentedControl,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconUpload, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  intakeCandidate, createCandidate, bulkFromLinkedIn, addProjectCandidate, getProjects,
  getEnrichmentCostBasis,
} from "../api";
import CompanyAutocomplete from "./CompanyAutocomplete";
import GeoLocationAutocomplete from "./GeoLocationAutocomplete";
import InfoHint from "./InfoHint";
import { usePlan } from "../hooks/usePlan";

// Label + a hover info icon, for form fields whose behaviour needs a short explanation.
// Hover Tooltip (not a click Popover) so clicking near the field label can't toggle it.
const labelWithHint = (text, i18nKey) => (
  <Group gap={4} align="center" component="span" display="inline-flex">
    <span>{text}</span>
    <InfoHint i18nKey={i18nKey} width={300} />
  </Group>
);

// A dimmed note with a trailing info icon that flows WITH the text: the icon is glued to the
// last word in a no-wrap inline-flex span, so a line break moves word+icon together instead of
// stranding the icon alone on the next line (which a flex Group next to the Text would do).
const noteWithHint = (text, hint) => {
  const cut = text.lastIndexOf(" ");
  return (
    <Text size="xs" c="dimmed">
      {cut === -1 ? "" : text.slice(0, cut + 1)}
      <Box component="span" display="inline-flex"
        style={{ whiteSpace: "nowrap", alignItems: "center", gap: 4, verticalAlign: "bottom" }}>
        {cut === -1 ? text : text.slice(cut + 1)}
        {hint}
      </Box>
    </Text>
  );
};

const EMPTY_BASICS = {
  firstName: "", lastName: "", title: "", company: "", location: "", email: "", phone: "",
};

// A filled dot marking a tab that holds input. Creating only submits the open tab, so the
// dot is what keeps content on the other tabs from being invisibly ignored.
const contentDot = (
  <Box component="span" w={6} h={6} bg="teal.6"
    style={{ borderRadius: "50%", display: "inline-block", flexShrink: 0 }} />
);

/**
 * One "Add candidate" entry point with three explicit modes, one per creation mechanism:
 *   file    — dropped files become ONE contact (attachments + CV parse; identity read from content)
 *   linkedin — bulk: one contact per pasted URL
 *   manual  — a typed single contact
 * The mode picker replaces the old everything-at-once form, whose silent fork inference
 * (files vs URLs vs typed fields) is what confused people. Each mode carries only its own
 * inputs — corrections and extras (LinkedIn URL on a CV contact, details on any) belong in
 * edit mode after creation, and bulk ZIP/VCF import stays on the Import page. Submitting
 * only reads the open mode; other modes keep their input (marked with a dot) until the
 * dialog closes. Reachable from global Contacts and from inside a project (where `project`
 * pre-fills the destination).
 */
export default function AddCandidateDialog({ opened, onClose, project = null, onCreated }) {
  const { t, i18n } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const { creditsBalance, loaded: planLoaded } = usePlan();

  const [mode, setMode] = useState("file");
  const [files, setFiles] = useState([]);
  const [urlsText, setUrlsText] = useState(""); // linkedin mode: one URL per line
  const [basics, setBasics] = useState(EMPTY_BASICS);
  const [companyId, setCompanyId] = useState(null);
  const [geoLocation, setGeoLocation] = useState(null); // structured pick from the location autocomplete
  const [projectId, setProjectId] = useState(project?.id ?? null);
  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [costBasis, setCostBasis] = useState(null);

  // Keep the destination in sync with the host (project drawer vs global contacts).
  useEffect(() => { setProjectId(project?.id ?? null); }, [project?.id]);

  // Only need the project list when there's no pre-filled destination.
  useEffect(() => {
    if (!opened || project) return;
    getProjects().then((p) => setProjects(Array.isArray(p) ? p : [])).catch(() => {});
  }, [opened, project]);

  // Tenant rate card for the live credit estimate — fetched once when first opened.
  useEffect(() => {
    if (!opened || costBasis) return;
    getEnrichmentCostBasis().then(setCostBasis).catch(() => {});
  }, [opened, costBasis]);

  const anyTyped = Object.values(basics).some((v) => v.trim());

  function setField(key, value) {
    setBasics((b) => ({ ...b, [key]: value }));
  }

  function reset() {
    setMode("file");
    setFiles([]); setUrlsText(""); setBasics(EMPTY_BASICS); setCompanyId(null); setGeoLocation(null);
  }

  function close() {
    if (submitting) return;
    reset();
    onClose();
  }

  function addFiles(dropped) {
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...dropped.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  }

  function removeFile(idx) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  // Dropping a file anywhere on the dialog means file mode — switch on drag-over so the
  // dropzone is already visible when the drop lands, and catch drops outside it too.
  // (addFiles dedupes by name:size, so a drop that both the dropzone and this handler see
  // is added once.)
  function handleDragOver(e) {
    if (!Array.from(e.dataTransfer?.types ?? []).includes("Files")) return;
    e.preventDefault();
    if (mode !== "file") setMode("file");
  }

  function handleDrop(e) {
    const dropped = Array.from(e.dataTransfer?.files ?? []);
    if (dropped.length === 0) return;
    e.preventDefault();
    if (!submitting) addFiles(dropped);
  }

  async function finish(result, { single }) {
    onCreated?.(result);
    reset();
    onClose();
    // From global Contacts a single new candidate jumps to its detail page (where the enrichment
    // badge and the soon-to-be-filled name live). From a project we stay on the board (the host
    // reloaded via onCreated). Bulk results stay put and surface a summary toast.
    if (!project && single && result?.id) setLocation(`/candidates/${result.id}`);
  }

  function parsedUrls() {
    return urlsText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // File mode: ONE candidate, every file an attachment. Identity (name, contact details,
      // LinkedIn URL) is read from the content during enrichment.
      if (mode === "file") {
        if (files.length === 0) {
          notifications.show({ color: "yellow", message: t("add_candidate_no_files") });
          return;
        }
        const result = await intakeCandidate({ files, projectId: projectId || undefined });
        notifications.show({ color: "green", title: t("add_candidate_created"), message: result?.name });
        await finish(result, { single: true });
        return;
      }

      // LinkedIn mode: bulk path (1..N; placeholder names filled by enrichment).
      if (mode === "linkedin") {
        const urls = parsedUrls();
        if (urls.length === 0) {
          notifications.show({ color: "yellow", message: t("add_candidate_no_urls") });
          return;
        }
        const r = await bulkFromLinkedIn(projectId || null, urls);
        notifications.show({
          color: "green",
          title: t("bulk_done_title"),
          message: t("bulk_result_summary", {
            created: r.created ?? 0,
            addedExisting: r.addedExisting ?? 0,
            alreadyOnProject: r.alreadyOnProject ?? 0,
            invalid: (r.invalidUrls ?? []).length,
          }),
        });
        await finish(r, { single: false });
        return;
      }

      // Manual mode: single JSON create. Name required here.
      if (!basics.lastName.trim()) {
        notifications.show({ color: "yellow", message: t("last_name_required") });
        return;
      }
      const dto = buildCreateDto(basics, companyId, geoLocation);
      const candidate = await createCandidate(dto);
      if (projectId) {
        try {
          await addProjectCandidate(projectId, { candidateId: candidate.id });
        } catch {
          notifications.show({ color: "red", title: t("error"), message: t("add_candidate_failed") });
        }
      }
      notifications.show({ color: "green", title: t("add_candidate_created"), message: candidate.name });
      await finish(candidate, { single: true });
    } catch (e) {
      if (e?.status !== 402) {
        notifications.show({ color: "red", title: t("error"), message: "" + (e?.message || e) });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Live, advisory credit estimate for the open mode's inputs + the tenant rate card.
  const estUrls = parsedUrls();
  const est = estimateEnrichment(costBasis, mode, { fileCount: files.length, urlCount: estUrls.length });
  const estVisible = !!costBasis && !!est
    && (mode === "file" ? files.length > 0 : mode === "linkedin" ? estUrls.length > 0 : anyTyped);
  const isEur = (i18n.language || "").toLowerCase().startsWith("de");
  const per1000 = costBasis ? (isEur ? costBasis.eurPer1000Credits : costBasis.usdPer1000Credits) : 0;
  const sym = isEur ? "€" : "$";
  const fmtPrice = (credits) => `${sym}${((credits / 1000) * per1000).toFixed(2)}`;

  const modeLabel = (label, hasContent) => (
    <Group gap={6} justify="center" wrap="nowrap">
      <span>{label}</span>
      {hasContent && contentDot}
    </Group>
  );

  const submitLabel = mode === "linkedin" && estUrls.length > 1
    ? t("add_candidate_create_many", { count: estUrls.length })
    : t("create");

  return (
    <Modal opened={opened} onClose={close} title={t("add_candidate_dialog_title")} size="lg">
      <Stack onDragOver={handleDragOver} onDrop={handleDrop}>
        <SegmentedControl
          fullWidth
          value={mode}
          onChange={setMode}
          disabled={submitting}
          data={[
            { value: "file", label: modeLabel(t("add_candidate_tab_file"), files.length > 0) },
            { value: "linkedin", label: modeLabel("LinkedIn", estUrls.length > 0) },
            { value: "manual", label: modeLabel(t("add_candidate_tab_manual"), anyTyped) },
          ]}
        />

        {mode === "file" && (
          <>
            <Dropzone onDrop={addFiles} multiple p="md" disabled={submitting}>
              <Group justify="center" gap="md" style={{ pointerEvents: "none" }}>
                <Dropzone.Accept>
                  <IconUpload size={32} stroke={1.5} color="var(--mantine-color-blue-6)" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={32} stroke={1.5} color="var(--mantine-color-red-6)" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconUpload size={32} stroke={1.5} color="var(--mantine-color-dimmed)" />
                </Dropzone.Idle>
                <div>
                  <Text size="sm" fw={500}>{t("dropzone_title")}</Text>
                  <Text size="xs" c="dimmed">{t("add_candidate_files_hint")}</Text>
                </div>
              </Group>
            </Dropzone>

            {files.length > 0 && (
              <Group gap="xs">
                {files.map((f, i) => (
                  <Pill key={`${f.name}:${f.size}:${i}`} withRemoveButton onRemove={() => removeFile(i)}>
                    {f.name}
                  </Pill>
                ))}
              </Group>
            )}
          </>
        )}

        {mode === "linkedin" && (
          <Textarea
            label={t("add_candidate_urls_label")}
            placeholder={t("linkedin_urls_placeholder")}
            value={urlsText}
            onChange={(e) => setUrlsText(e.currentTarget.value)}
            autosize
            minRows={3}
            maxRows={10}
            disabled={submitting}
          />
        )}

        {mode === "manual" && (
          <Stack gap="xs">
            <Group grow>
              <TextInput label={t("first_name")} placeholder={t("first_name")}
                value={basics.firstName} onChange={(e) => setField("firstName", e.currentTarget.value)} />
              <TextInput label={t("last_name")} placeholder={t("last_name")} required
                value={basics.lastName} onChange={(e) => setField("lastName", e.currentTarget.value)} />
            </Group>
            <TextInput label={t("title")} placeholder={t("title_placeholder")}
              value={basics.title} onChange={(e) => setField("title", e.currentTarget.value)} />
            <CompanyAutocomplete label={labelWithHint(t("company"), "company_field_help")} value={basics.company}
              onChange={({ name, id }) => { setField("company", name); setCompanyId(id); }} />
            <GeoLocationAutocomplete value={basics.location}
              onChange={({ location, geoLocation: geo }) => { setField("location", location); setGeoLocation(geo); }} />
            <TextInput label={t("email")} placeholder={t("email_placeholder")}
              value={basics.email} onChange={(e) => setField("email", e.currentTarget.value)} />
            <TextInput label={t("phone")} placeholder={t("phone_placeholder")}
              value={basics.phone} onChange={(e) => setField("phone", e.currentTarget.value)} />
          </Stack>
        )}

        {noteWithHint(t("add_candidate_enrich_note"),
          <InfoHint i18nKey="enrichment_intake_help" multiline width={330} />)}

        {project ? (
          <Text size="sm" c="dimmed">{t("add_candidate_added_to_project", { project: project.title })}</Text>
        ) : (
          <Select
            label={t("assign_to_project_optional")}
            placeholder={t("filter_by_project")}
            data={projects.map((p) => ({ value: p.id, label: p.title }))}
            value={projectId}
            onChange={setProjectId}
            searchable
            clearable
          />
        )}

        {estVisible && (
          <Group gap={8} align="center" wrap="wrap">
            <Text size="xs" c="dimmed">
              {t("add_candidate_est_cost")}:{" "}
              {est.count > 1
                ? t("add_candidate_est_value_many", { credits: est.totalCredits, count: est.count, price: fmtPrice(est.totalCredits) })
                : t("add_candidate_est_value_one", { credits: est.totalCredits, price: fmtPrice(est.totalCredits) })}
            </Text>
            <InfoHint multiline width={340} label={t("add_candidate_est_hint", {
              cv: costBasis.cvExtractionCredits,
              li: costBasis.linkedInFetchCredits,
              web: costBasis.webSearchCredits,
              idx: (costBasis.searchProfileCredits || 0) + (costBasis.embeddingCredits || 0),
              price1k: `${sym}${per1000.toFixed(2)}`,
            })} />
            {planLoaded && (
              <Text size="xs" c={creditsBalance < est.totalCredits ? "red" : "dimmed"}>
                · {t("add_candidate_est_balance", { balance: creditsBalance })}
              </Text>
            )}
          </Group>
        )}

        <Divider />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={close} disabled={submitting}>{t("cancel")}</Button>
          <Button onClick={handleSubmit} loading={submitting}>{submitLabel}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Rough, advisory pre-flight credit estimate for the open mode + the tenant rate card. It
// mirrors the three submit modes in handleSubmit and is deliberately an upper bound: it always
// includes web search, which the backend skips when disabled or context is thin, so the real charge
// is often lower. The backend job handler stays the authoritative charger — this only sets expectations.
function estimateEnrichment(cb, mode, { fileCount, urlCount }) {
  if (!cb) return null;
  // Base of the estimate = the three charges any enrichment may incur, counted as an upper bound:
  // search profile + embedding + web search. The backend skips each when there's no content or the
  // feature is off, so the real charge is often lower (see the upper-bound note above).
  const base = (cb.searchProfileCredits || 0) + (cb.embeddingCredits || 0) + (cb.webSearchCredits || 0);

  if (mode === "file") {
    // One candidate, dropped files → CV parse + per-file classification (+ LinkedIn fetch when a
    // URL is found in the content — counted, upper bound).
    const c = base + (cb.cvExtractionCredits || 0) + (cb.documentClassificationCredits || 0) * fileCount
      + (cb.linkedInFetchCredits || 0);
    return { totalCredits: c, count: 1 };
  }
  if (mode === "linkedin") {
    // Bulk from URLs → one enrichment per URL, each a LinkedIn fetch.
    const per = base + (cb.linkedInFetchCredits || 0);
    return { totalCredits: per * Math.max(urlCount, 1), count: Math.max(urlCount, 1) };
  }
  // Manual: typed single candidate.
  return { totalCredits: base, count: 1 };
}

function buildCreateDto(basics, companyId, geoLocation) {
  const dto = { firstName: basics.firstName.trim(), lastName: basics.lastName.trim() };
  if (basics.title.trim()) dto.title = basics.title.trim();
  if (basics.location.trim()) dto.location = basics.location.trim();
  // A picked autocomplete suggestion carries the structured geo; free-typed text doesn't (the
  // component clears it), so manual adds get canonical coordinates without waiting for enrichment.
  if (geoLocation) dto.geoLocation = geoLocation;
  if (basics.phone.trim()) dto.phone = basics.phone.trim();
  if (basics.email.trim()) dto.email = basics.email.trim();
  if (basics.company.trim()) {
    dto.career = [{
      role: basics.title.trim() || "—",
      companyName: basics.company.trim(),
      companyId,
      from: new Date().getFullYear().toString(),
      to: "",
    }];
  }
  return dto;
}
